import { useState, useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/hooks/use-subscription";
import { MobileWarningDialog } from "@/components/auth/MobileWarningDialog";

const ProtectedRoute = () => {
  const { isAuthenticated, loading: authLoading, signOut } = useAuth();
  const { hasAccess, subscriptionStatus, loading: subLoading } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track if we've completed initial auth check - prevents unmount on re-validation
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  useEffect(() => {
    if (!authLoading && !subLoading) {
      setInitialLoadComplete(true);
    }
  }, [authLoading, subLoading]);

  // Handle stale session - server says unauthenticated but we think we're logged in
  useEffect(() => {
    if (!authLoading && !subLoading && isAuthenticated && subscriptionStatus === "unauthenticated") {
      console.warn("Stale session detected in ProtectedRoute, redirecting to auth");
      signOut().then(() => {
        navigate("/auth", { replace: true });
      });
    }
  }, [authLoading, subLoading, isAuthenticated, subscriptionStatus, signOut, navigate]);

  // Allow access to pricing page even without subscription
  const isPricingPage = location.pathname === "/pricing";

  // Only show loading spinner on INITIAL load, not re-validation
  if ((authLoading || subLoading) && !initialLoadComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // If on pricing page, always allow
  if (isPricingPage) {
    return <Outlet />;
  }

  // If no access, redirect to pricing
  if (!hasAccess) {
    return <Navigate to="/pricing" replace />;
  }

  return (
    <>
      <MobileWarningDialog />
      <Outlet />
    </>
  );
};

export default ProtectedRoute;
